import { useState } from 'react';
import { FileText, ChevronRight, Search, Scale, Shield, Clock, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/ui/Footer';

interface TermsSection {
  id: string;
  title: string;
  description: string;
  content: React.ReactNode;
}

export default function Terms() {
  const [activeSection, setActiveSection] = useState('general-terms');
  const [searchTerm, setSearchTerm] = useState('');

  const termsSections: TermsSection[] = [
    {
      id: 'general-terms',
      title: 'Términos Generales',
      description: 'Condiciones básicas del servicio.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Términos Generales</h3>
          <p className="text-slate-600 leading-relaxed">
            Al utilizar Validea, aceptas cumplir y estar sujeto a estos términos y condiciones de uso.
            Es importante que los leas detenidamente antes de comenzar a usar nuestros servicios.
          </p>

          <div className="mt-6 space-y-4">
            <h4 className="text-lg font-medium text-slate-900">Definiciones Importantes</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  term: 'Servicio',
                  definition: 'La plataforma Validea y todas sus funcionalidades'
                },
                {
                  term: 'Usuario',
                  definition: 'Persona o entidad que utiliza la plataforma'
                },
                {
                  term: 'MVP',
                  definition: 'Producto Mínimo Viable creado en la plataforma'
                },
                {
                  term: 'Contenido',
                  definition: 'Datos, textos, gráficos y otros materiales'
                }
              ].map((item, index) => (
                <div key={index} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h5 className="font-medium text-slate-900 mb-2">{item.term}</h5>
                  <p className="text-sm text-slate-600">{item.definition}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'service-terms',
      title: 'Términos del Servicio',
      description: 'Condiciones específicas de uso del servicio.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Términos del Servicio</h3>
          <p className="text-slate-600 leading-relaxed">
            Estos términos establecen las condiciones específicas bajo las cuales proporcionamos nuestros servicios.
          </p>

          <div className="space-y-4">
            {[
              {
                title: 'Registro de Cuenta',
                items: [
                  'Debes proporcionar información precisa y completa',
                  'Eres responsable de mantener la seguridad de tu cuenta',
                  'No compartir credenciales de acceso'
                ]
              },
              {
                title: 'Uso del Servicio',
                items: [
                  'Uso exclusivo para fines legales',
                  'No interferir con el funcionamiento del servicio',
                  'Respetar los límites de uso establecidos'
                ]
              },
              {
                title: 'Contenido del Usuario',
                items: [
                  'Mantienes los derechos de tu contenido',
                  'Nos otorgas licencia para mostrar tu contenido',
                  'No debes infringir derechos de terceros'
                ]
              }
            ].map((section, index) => (
              <div key={index} className="bg-white rounded-lg border border-slate-200 p-4">
                <h4 className="font-medium text-slate-900 mb-3">{section.title}</h4>
                <ul className="space-y-2">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-2 text-sm text-slate-600">
                      <FileText className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'limitations',
      title: 'Limitaciones',
      description: 'Restricciones y límites del servicio.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Limitaciones del Servicio</h3>
          <p className="text-slate-600 leading-relaxed">
            Existen ciertas limitaciones y restricciones en el uso de nuestros servicios que debes conocer.
          </p>

          <div className="bg-primary-50 border border-primary-100 rounded-lg p-6">
            <h4 className="text-lg font-medium text-primary-900 mb-4">Límites Técnicos</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Máximo de 10 MVPs por cuenta',
                'Límite de 1000 visitas por hora',
                'Almacenamiento de 1GB por MVP',
                'Máximo 5 usuarios por cuenta',
                'Retención de datos por 12 meses',
                'API rate limit de 100 req/min'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-primary-200">
                  <FileText className="w-4 h-4 text-primary-600" />
                  <span className="text-sm text-primary-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'termination',
      title: 'Terminación',
      description: 'Condiciones de terminación del servicio.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Terminación del Servicio</h3>
          <p className="text-slate-600 leading-relaxed">
            Las condiciones bajo las cuales se puede terminar el servicio y sus consecuencias.
          </p>

          <div className="space-y-4">
            {[
              {
                title: 'Por parte del Usuario',
                description: 'Puedes cancelar tu cuenta en cualquier momento'
              },
              {
                title: 'Por Incumplimiento',
                description: 'Podemos terminar el servicio por violación de términos'
              },
              {
                title: 'Consecuencias',
                description: 'Pérdida de acceso a datos y servicios'
              },
              {
                title: 'Periodo de Gracia',
                description: '30 días para exportar tus datos'
              }
            ].map((item, index) => (
              <div key={index} className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-700">{index + 1}</span>
                  </div>
                  <h5 className="font-medium text-slate-900">{item.title}</h5>
                </div>
                <p className="text-sm text-slate-600 ml-11">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  const filteredSections = termsSections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 bg-gray-50">
        {/* Enhanced Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <nav className="mb-4">
              <ol className="flex items-center space-x-2 text-sm text-gray-500">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors">Inicio</Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className="text-gray-900 font-medium">Términos y Condiciones</li>
              </ol>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Términos y Condiciones
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Conoce las condiciones y reglas que rigen el uso de nuestros servicios para garantizar una experiencia segura y transparente.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 rounded-lg p-2">
                    <Scale className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Marco Legal</h3>
                    <p className="text-sm text-gray-500">Cumplimiento normativo</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 rounded-lg p-2">
                    <Shield className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Protección</h3>
                    <p className="text-sm text-gray-500">Garantías y seguridad</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 rounded-lg p-2">
                    <Clock className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Vigencia</h3>
                    <p className="text-sm text-gray-500">Términos actualizados</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Te recomendamos leer detenidamente estos términos antes de usar nuestros servicios. 
                    Al utilizar Validea, aceptas cumplir con todas las condiciones aquí establecidas.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search Bar with improved styling */}
          <div className="mb-12">
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar en los términos y condiciones..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-500"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500 text-center">
                Última actualización: Marzo 2024
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <nav className="lg:col-span-1">
              <div className="space-y-1">
                {termsSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </nav>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {termsSections.find((section) => section.id === activeSection)?.content}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
} 